import { Input, Layout, Modal, Spin } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import EnterFullScreen from 'assets/Icons/fullscreen/EnterFullScreen';
import ExitFullScreen from 'assets/Icons/fullscreen/ExitFullScreen';
import clsx from 'clsx';
import { motion, useAnimation } from 'framer-motion';
import moment, { Moment } from 'moment';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import ReactPlayer from 'react-player';
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';
import { useRecoilState, useRecoilValue } from 'recoil';
import { UAParser } from 'ua-parser-js';

import { useReadingTracker } from '@/hooks/useReadingTracker';

import CallbackModal from '@/components/CoursesV2/CallbackModal';
import { Note } from '@/components/PdfReader/GeneratePdf/NotePdfDocument';
import PageNavigator from '@/components/PdfReader/PageNavigator';
import NotesApp from '@/components/PdfReader/PdfNotes/PdfNotes';
import TakeNotePanel from '@/components/PdfReader/PdfNotes/TakeNotePanel';
import ShopModal from '@/components/PdfReader/ShopModal';
import ToolBoxSidebar from '@/components/PdfReader/ToolBoxSidebar';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { generateAndDownloadPdf } from '@/components/shared/generateAndDownloadPdf';
import { notify } from '@/components/shared/notify';

import { callBackModalAtom } from '@/atoms/callBackModalAtom';
import { aiModalOpenAtom } from '@/atoms/pdfStates/aiModalOpenAtom';
import { drawerPdfAtom } from '@/atoms/pdfStates/drawerAtom';
import { isFullScreenAtom } from '@/atoms/pdfStates/isFullScreen';
import { notesAtom } from '@/atoms/pdfStates/notesAtom';
import { pageNumberAtom } from '@/atoms/pdfStates/pageNumber';
import { takeNoteModalAtom } from '@/atoms/pdfStates/takeNoteModalAtom';
import { shopModalAtom } from '@/atoms/shopModalAtom';
import { EventV2 } from '@/utils/Events/events';
import { normalizeProductName } from '@/utils/index';

import AiChatDrawer from './AiChatDrawer';
import AudioPlayer from './AudioPlayer';
import ChapterDrawer from './ChapterDrawer';
import FailedToLoadPDF from './FailedToLoadPDF';
import PdfReaderHeader from './PdfReaderHeader';
import ShareButton from './ShareButton';
import PlayerWithChapters from './VideoSummary/PlayerWithChapters';
import ButtonDefault, { ButtonVariants } from '../shared/basic/button';
import { ShouldRender } from '../shared/basic/ShouldRender';
import {
  getBookToken as cookieBookToken,
  getLoginToken,
  setBookToken,
} from '../shared/getToken';
import { LoadingV2 } from '../shared/LoadingV2';
import { tokenUpdate } from '../../atoms/authModalControl';
import { user } from '../../atoms/user';
import { EventDetails, logEvent } from '../../hooks/useClickEventTracking';
import { Endpoints } from '../../network';
import {
  getAllChaptersApi,
  getBookByIdApi,
  getBookToken,
  getPdfStreamStatus,
  postReadingLogs,
  retryPdfJob,
} from '../../services/Library/Books';
import { MyBookByIDInterface } from '../../utils/interfaces/Library/Books';
import {
  AllChaptersListInterface,
  Chapter,
} from '../../utils/interfaces/Library/Pdf';
import { User } from '../../utils/interfaces/User';
import { FailedToLoadPdf, NetworkError } from '../../../assets';

import {
  ReadingSourceType,
  // RewardUnlockedEvent,
  // SessionAutoClosedEvent,
  // TickRejectedEvent,
} from '@/types/websocket';

const SelectionTooltip = dynamic(() => import('./PdfNotes/SelectionTooltip'), {
  ssr: false,
});

interface RotationsState {
  [key: number]: number;
}

export interface ITakeNodeModal {
  value: boolean;
  pageNumber: number;
  selectedText: string;
  selectionRect?: DOMRect | null;
  noteId?: string;
}

const { Content } = Layout;
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface Props {
  resource: string | null;
  jobId: string | null;
  pdfStatus?: 'COMPLETED' | 'PENDING' | 'FAILED';
}

const PDFViewer: React.FC<Props> = ({ resource, jobId, pdfStatus }) => {
  const router = useRouter();
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  const baseUrl = process.env.NEXT_PUBLIC_API_READER_URL;
  const screens = useBreakpoint();

  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useRecoilState(pageNumberAtom);
  const [scale, setScale] = useState(1.0);
  const [rotations, setRotations] = useState<RotationsState>({});
  const [isFullScreen, setIsFullScreen] = useRecoilState(isFullScreenAtom);
  const [sideBySide, setSideBySide] = useState(true);
  const [zoomValue, setZoomValue] = useState(100);
  const [samplePdf, setSamplePdf] = useState<string>();
  const [openDrawer, setOpenDrawer] = useRecoilState<boolean>(aiModalOpenAtom);
  const [duration, setDuration] = useState(0);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const userData: User | undefined = useRecoilValue(user);
  const leftButtonRef = useRef<HTMLButtonElement>(null);
  const rightButtonRef = useRef<HTMLButtonElement>(null);
  const [audioBook, setAudioBook] = useState<{
    bookId: string;
    token: string;
  }>();
  const [isGenerating, setIsGenerating] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const [windowHeight, setWindowHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0,
  );
  const {
    book_id,
    chapterPageNumber,
    samplePdf: samplePdfValue,
    chapterId,
  } = router.query;
  const controls = useAnimation();
  const [bookmarks, setBookmarks] = useState<{ page: number; label: string }[]>(
    [],
  );
  const [label, setLabel] = useState('');
  const [visible, setVisible] = useState(true);
  const notes = useRecoilValue<Record<string, Note[]>>(notesAtom);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCompletedModalOpen, setIsCompletedModalOpen] = useState(false);
  const [hasShownCompletedModal, setHasShownCompletedModal] = useState(false);
  const [chapterFieldId, setChapterFieldId] = useState('');
  const [startPage, setStartPage] = useState<number>(0);
  const [token, setToken] = useState('');
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const drawerState = useRecoilValue(drawerPdfAtom);
  const [chaptersList, setChaptersList] = useState<AllChaptersListInterface>();
  const [booksDetails, setBooksDetails] = useState<MyBookByIDInterface>();
  const [isInteracted, setIsInteracted] = useState(false);
  const [takeNoteModal, setTakeNoteModal] = useRecoilState(takeNoteModalAtom);
  const [showAudio, setShowAudio] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [renderPdf, setRenderPdf] = useState('');
  const [timeStamp, setTimeStamps] = useState({
    startTime: moment().format('YYYY-MM-DDTHH:mm:ss'),
    endTime: '',
  });
  const [isCallbackModal, setIsCallbackModal] =
    useRecoilState(callBackModalAtom);
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const [completeBookTimestamps, setCompleteBookTimestamps] = useState<{
    timeStamp: { page: number; start: number; end: number }[];
    startingPage: number;
  }>();

  const [hasError, setHasError] = useState(false);
  const [errorType, setErrorType] = useState<
    'server-timeout' | 'default' | null
  >(null);
  const [openShopModal, setOpenShopModal] = useRecoilState(shopModalAtom);
  const [seekTime, setSeekTime] = useState(0);
  const pageTimeSpentRef = useRef(0);
  const pageStartTimeRef = useRef<string>(moment().toISOString());
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 4000;
  const MAX_POLL_DURATION = 1 * 60 * 1000;

  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<ReactPlayer | null>(null);
  const pageNavigatorRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const pageInputRef = useRef<HTMLInputElement>(null);
  const takeNotePanelRef = useRef<HTMLDivElement>(null);
  const fullscreenRootRef = useRef<HTMLDivElement>(null);

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setLabel('');
  };

  const showDrawer = () => {
    setVisible(!visible);
  };

  const onClose = () => {
    setVisible(false);
  };

  const onDocumentLoadSuccess = async (pdf: any) => {
    try {
      setNumPages(pdf.numPages);

      const page = await pdf.getPage(1);
      const { width, height } = page.getViewport({ scale: 1 });
      setPageDimensions({ width, height });
    } catch (error) {
      console.error('Failed to load PDF:', error);
    }
  };

  const rotateLeft = (pageNumber: number) => {
    setRotations((prevRotations) => ({
      ...prevRotations,
      [pageNumber]: (prevRotations[pageNumber] || 0) - 90,
    }));
  };

  const rotateRight = (pageNumber: number) => {
    setRotations((prevRotations) => ({
      ...prevRotations,
      [pageNumber]: (prevRotations[pageNumber] || 0) + 90,
    }));
  };

  useEffect(() => {
    if (transformRef.current) {
      transformRef.current.centerView();
    }
  }, [sideBySide]);

  const getNextPage = async () => {
    if (pageNumber + 1 <= (numPages || 1)) {
      if (sideBySide) {
        if ((numPages || 0) < pageNumber + 1 + 2 && !samplePdf) {
          return numPages;
        } else {
          return pageNumber + 2;
        }
      } else {
        return pageNumber + 1;
      }
    }

    return pageNumber;
  };

  const getPreviousPage = async () => {
    if (pageNumber === 2 && sideBySide) {
      toggleSideBySide();
      return pageNumber - 1;
    }
    if (pageNumber > 1) {
      return sideBySide ? pageNumber - 2 : pageNumber - 1;
    }

    return pageNumber;
  };


  const useCurrentChapter = useMemo(() => {
    return (
      chaptersList?.chapters?.find(
        (chapter) =>
          chapter &&
          chapter?.endPage &&
          pageNumber >= chapter.startPage &&
          pageNumber <= chapter?.endPage,
      ) || null
    );
  }, [pageNumber, book_id, takeNoteModal.value, chaptersList]);

  const handleReadingLogs = () => {
    try {
      if (pageNumber) {
        postReadingLogs({
          book: book_id as string,
          startTime: timeStamp.startTime,
          endTime: moment().format('YYYY-MM-DDTHH:mm:ss'),
          pageNumber: pageNumber,
          chapterPageNumber: pageNumber,
          ...(samplePdf
            ? { sample: book_id as string }
            : chapterFieldId
              ? { chapter: chapterFieldId }
              : {}),
        });
      }

      const duration = moment().diff(timeStamp.startTime, 'milliseconds');

      // Wait for book details to be loaded before firing analytics events.
      // On initial render booksDetails may still be undefined, which would
      // produce "Missing required field: source_name / sub_source_name" errors.
      if (!booksDetails?.title) return;

      const baseEventParams = {
        event_name: 'read',
        source: 'books',
        source_type: samplePdf ? 'short' : 'long',
        source_id: book_id as string,
        sub_source: samplePdf ? null : 'chapter',
        source_name: booksDetails?.ecomData?.display_name ? booksDetails.ecomData?.display_name : booksDetails?.title
          ? normalizeProductName(booksDetails?.title)
          : booksDetails?.title,
        sub_source_id: samplePdf
          ? (book_id as string)
          : chapterFieldId,
        sub_source_name: samplePdf
          ? (booksDetails?.title
            ? normalizeProductName(booksDetails?.title)
            : booksDetails?.title)
          : useCurrentChapter?.name,
        meta: {
          book_page_no: samplePdf ? pageNumber : startPage + pageNumber - 1,
          book_total_pages: numPages,
          product_category: booksDetails?.categories?.[0]?.name,
          click_action: 'read',
          ...(!samplePdf && { chapter_page_no: pageNumber }),
        },
      };

      const logReadEvent = (
        start: Moment,
        end: Moment,
        meta: Record<string, any>,
      ) => {
        const durationSeconds = Math.round(
          end.diff(start, 'milliseconds') / 1000,
        );
        logEvent({
          eventDetails: {
            ...baseEventParams,
            value: String(durationSeconds),
            activity_start: start.format('YYYY-MM-DDTHH:mm:ss'),
            activity_end: end.format('YYYY-MM-DDTHH:mm:ss'),
            meta: {
              ...baseEventParams.meta,
              ...meta,
            },
          },
          userData,
          pathname: router.pathname,
        });
      };

      if (sideBySide) {
        const halfDuration = duration / 2;
        const firstEventEndTime = moment(timeStamp.startTime)
          .clone()
          .add(halfDuration, 'milliseconds');
        const firstMeta = {
          book_page_no: samplePdf ? pageNumber - 2 : startPage + pageNumber - 3,
          ...(!samplePdf && { chapter_page_no: pageNumber - 2 }),
        };

        const secondMeta = {
          book_page_no: samplePdf ? pageNumber - 1 : startPage + pageNumber - 2,
          ...(!samplePdf && { chapter_page_no: pageNumber - 1 }),
        };
        logReadEvent(moment(timeStamp.startTime), firstEventEndTime, firstMeta);
        logReadEvent(firstEventEndTime, moment(), secondMeta);
      } else {
        const meta = {
          book_page_no: samplePdf ? pageNumber - 1 : startPage + pageNumber - 2,
          ...(!samplePdf && { chapter_page_no: pageNumber - 1 }),
        };
        logReadEvent(moment(timeStamp.startTime), moment(), meta);
      }

      setTimeStamps({
        startTime: moment().format('YYYY-MM-DDTHH:mm:ss'),
        endTime: '',
      });
    } catch (error) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  useEffect(() => {
    if (book_id || samplePdf) {
      handleReadingLogs();
      setChapterFieldId(useCurrentChapter?._id as string);
    }
  }, [pageNumber]);

  useEffect(() => {
    if (numPages && pageNumber === numPages) {
      if (!hasShownCompletedModal) {
        setIsCompletedModalOpen(true);
        setHasShownCompletedModal(true);
      }
    } else {
      setHasShownCompletedModal(false);
    }
  }, [pageNumber, numPages, hasShownCompletedModal]);

  const handlePageTransition = async (newPageNumber: number) => {
    if (newPageNumber >= 1 && newPageNumber <= (numPages || 1)) {
      setPageNumber(newPageNumber);
    }
  };

  const addBookmark = () => {
    if (label) {
      setBookmarks([...bookmarks, { page: pageNumber, label }]);
      handleCancel();
    }
  };

  const zoomIn = () => {
    const newScale = scale + 0.1;
    handleZoomChange(newScale);
  };

  const zoomOut = () => {
    const newScale = scale - 0.1;
    handleZoomChange(newScale);
  };
  const handleGetDefaultBookById = async (bookId: string) => {
    try {
      const token = await handleGetBookToken();

      if (pdfStatus === 'COMPLETED' && resource && resource !== null) {
        setRenderPdf(
          `${baseUrl}/${Endpoints.getCompleteBookByBookId_Stream}/${bookId}?token=${token}`,
        );
        if (chapterPageNumber) {
          setPageNumber(+(chapterPageNumber as string));
        }
        setScale(1.0);
        setZoomValue(100);
        setHasError(false);
        setErrorType(null);
        return;
      }
      if (pdfStatus === 'PENDING' && jobId && jobId !== null) {
        pollPdfStatus(
          jobId,
          `${baseUrl}/${Endpoints.getCompleteBookByBookId_Stream}/${bookId}?token=${token || ''}`,
        );
        return;
      }

      setRenderPdf(
        `${baseUrl}/${Endpoints.getCompleteBookByBookId_Stream}/${bookId}?token=${token}`,
      );

      if (chapterPageNumber) {
        setPageNumber(+(chapterPageNumber as string));
      }
      setScale(1.0);
      setZoomValue(100);
      setHasError(false);
      setErrorType(null);
    } catch (error: any) {
      setHasError(true);

      if (
        error.code === 'ECONNABORTED' ||
        error.code === 'ECONNRESET' ||
        error.code === 'ERR_NETWORK' ||
        error.message?.toLowerCase().includes('timeout') ||
        error.message?.toLowerCase().includes('network') ||
        error.message?.toLowerCase().includes('fetch')
      ) {
        setErrorType('server-timeout');
      } else {
        setErrorType('default');
      }
    }
  };

  const handleRetry = async (jobId: string, bookName: string) => {
    try {
      await retryPdfJob(jobId, bookName);
    } catch (err: any) {
      notify(formateErrorObject(err), 'error');
    }
  };
  const pollPdfStatus = (jobId: string, apiUrl: string) => {
    let retryCount = 0;
    let elapsedTime = 0;
    let pollInterval: NodeJS.Timeout | null = null;

    setHasError(false);
    setErrorType(null);
    setIsGenerating(true);

    const startPolling = () => {
      pollInterval = setInterval(async () => {
        elapsedTime += 2000;

        if (elapsedTime >= MAX_POLL_DURATION) {
          if (pollInterval) clearInterval(pollInterval);
          setIsGenerating(false);
          setHasError(true);
          return;
        }

        try {
          const data = await getPdfStreamStatus(jobId);
          const status = data?.data?.data?.status;

          if (status === 'COMPLETED') {
            if (pollInterval) clearInterval(pollInterval);
            setIsGenerating(false);
            setRenderPdf(apiUrl);
          } else if (status === 'FAILED') {
            if (pollInterval) clearInterval(pollInterval);

            if (retryCount < MAX_RETRIES) {
              retryCount++;
              await handleRetry(
                jobId,
                booksDetails?.title || 'ZebraLearn Book',
              );

              setTimeout(startPolling, RETRY_DELAY);
            } else {
              setIsGenerating(false);
              setHasError(true);
            }
          }
        } catch (err) {
          if (pollInterval) clearInterval(pollInterval);
          setIsGenerating(false);
          setHasError(true);
          setErrorType('server-timeout');
        }
      }, 2000);
    };

    startPolling();
  };

  const isIphone = (): boolean => {
    const parser = new UAParser();
    const device = parser.getDevice();
    return device.model === 'iPhone';
  };
  const options = useMemo(
    () => ({
      password: resource,
    }),
    [resource],
  );

  const file = useMemo(() => samplePdf ?? renderPdf, [samplePdf, renderPdf]);
  const toggleFullScreen = () => {
    const layout = document.querySelector('.ant-layout-pdf') as any;

    if (layout) {
      if (!isFullScreen) {
        const fullscreenFunction =
          layout.requestFullscreen ||
          layout.webkitRequestFullscreen ||
          layout.msRequestFullscreen ||
          layout.mozRequestFullScreen;

        if (fullscreenFunction) {
          try {
            const orientation = screen.orientation as any;
            if (orientation && orientation.lock) {
              orientation.lock('portrait').catch((error: any) => {
                console.error('Failed to lock orientation:', error);
              });
            }

            // Using promise-based fullscreen API if available
            if (fullscreenFunction === layout.requestFullscreen) {
              fullscreenFunction.call(layout).catch((error: any) => {
                console.error('Failed to enter fullscreen mode:', error);
              });
            } else {
              fullscreenFunction.call(layout);
            }
          } catch (error) {
            console.error('Failed to enter fullscreen mode:', error);
          }
        } else {
          console.warn('Fullscreen API is not supported by this browser.');
        }
      } else {
        const docs = document as any;
        const exitFullscreenFunction =
          docs.exitFullscreen ||
          docs.webkitExitFullscreen ||
          docs.msExitFullscreen ||
          docs.mozCancelFullScreen;

        if (exitFullscreenFunction) {
          try {
            // Using promise-based exit fullscreen API if available
            if (exitFullscreenFunction === docs.exitFullscreen) {
              exitFullscreenFunction.call(document).catch((error: any) => {
                console.error('Failed to exit fullscreen mode:', error);
              });
            } else {
              exitFullscreenFunction.call(document);
            }

            setIsFullScreen(!isFullScreen);
          } catch (error) {
            console.error('Failed to exit fullscreen mode:', error);
          }
        } else {
          console.warn('Exit fullscreen API is not supported by this browser.');
        }
      }
    } else {
      console.warn('Layout element not found.');
    }
  };

  useEffect(() => {
    if (chapterPageNumber) {
      const pageData = completeBookTimestamps?.timeStamp.find(
        (item) =>
          item.page ===
          (+(chapterPageNumber as string) || 0) -
          completeBookTimestamps?.startingPage,
      );

      if (pageData) {
        setSeekTime(pageData.start);
      }
      setChapterFieldId(useCurrentChapter?._id as string);
    }
  }, [chapterPageNumber, completeBookTimestamps?.timeStamp, useCurrentChapter]);

  const forward15Seconds = () => {
    if (playerRef.current) {
      const currentTime = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(currentTime + 15, 'seconds');
    }
  };

  const backward15Seconds = () => {
    if (playerRef.current) {
      const currentTime = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(currentTime - 15, 'seconds');
    }
  };

  useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent) => {
      try {
        if (event.key === 'ArrowLeft' && !openDrawer) {
          if (playing) {
            backward15Seconds();
          } else {
            const previousPage = await getPreviousPage();
            handlePageTransition(previousPage);

            if (leftButtonRef.current) {
              leftButtonRef.current.click();
            }
          }
        } else if (event.key === 'ArrowRight' && !openDrawer) {
          if (playing) {
            forward15Seconds();
          } else {
            if (!(pageNumber >= 10 && !token)) {
              const nextPage = await getNextPage();
              handlePageTransition(nextPage as number);

              // Optional: focus on the right button when arrow right is pressed
              if (rightButtonRef.current) {
                rightButtonRef.current.click();
              }
            }
          }
        }
      } catch (error) {
        console.error('Error while handling keyboard input:', error);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleGetBookToken = async () => {
    const bookToken: string | null = await cookieBookToken();
    if (bookToken && bookToken !== 'undefined') return bookToken;

    const userToken = await getLoginToken();
    setToken(userToken as string);
    if (userToken) {
      try {
        const response = await getBookToken();
        await setBookToken(response?.data?.file_token);
        return encodeURIComponent(response?.data?.file_token as string);
      } catch (error) {
        return;
      }
    }
    return;
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(
        !!document.fullscreenElement ||
        !!(document as any).webkitFullscreenElement ||
        !!(document as any).msFullscreenElement ||
        !!(document as any).mozFullScreenElement,
      );
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('msfullscreenchange', handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullScreenChange,
      );
      document.removeEventListener(
        'msfullscreenchange',
        handleFullScreenChange,
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullScreenChange,
      );
    };
  }, []);

  const toggleSideBySide = () => {
    setSideBySide(!sideBySide);

    setPageNumber((prevPage) => {
      const newPage = prevPage % 2 !== 0 ? prevPage - 1 : prevPage;
      return newPage === 0 ? 1 : newPage;
    });
  };

  const getAllChaptersListData = async (book_id: string) => {
    try {
      const response = await getAllChaptersApi(book_id);
      setChaptersList(response);
      setChapterFieldId(chapterId ?? response?.chapters?.[0]?._id);
    } catch (error) {
      console.error('API call error:', error);
    }
  };

  const getBookDetailsById = async (bookId: string) => {
    try {
      const response = await getBookByIdApi(bookId);
      setBooksDetails(response);

      if (samplePdfValue) {
        setCompleteBookTimestamps({
          timeStamp:
            (response?.sampleBookTimestamps?.length ?? 0) > 0
              ? response?.sampleBookTimestamps
              : response?.completeBookTimestamps || [],
          startingPage:
            response?.sampleBookAudioStartingPage ||
            response?.completeBookAudioStartingPage ||
            1,
        });
      } else {
        setCompleteBookTimestamps({
          timeStamp: response?.completeBookTimestamps || [],
          startingPage: response?.completeBookAudioStartingPage || 1,
        });
      }
    } catch (error) {
      console.error('API call error:', error);
    }
  };
  const playAudio = async () => {
    const token = await handleGetBookToken();

    setShowAudio(!showAudio);

    setPlaying(!showAudio);

    if (!showAudio) {
      setAudioBook({
        bookId: book_id?.toString() as string,
        token: token as string,
      });
      setPageNumber(completeBookTimestamps?.startingPage || 0);
    }

    setVisible(false);
  };
  const [showVideoModal, setShowVideoModal] = useState(false);

  const redirectToVideoPage = () => {
    if (!book_id || !useCurrentChapter?._id) return;
    setShowVideoModal(true);
  };
  const handleGetSampleBookById = async (bookId: string) => {
    try {
      const url = `${baseUrl}/${Endpoints.getSampleBook}/${bookId}`;
      if (chapterPageNumber) {
        setPageNumber(+(chapterPageNumber as string));
      }

      if (!url) throw new Error('Invalid URL for the sample book');
      setSamplePdf(url);
      setHasError(false);
      setErrorType(null);
    } catch (error: any) {
      setHasError(true);
      if (
        error.code === 'ECONNABORTED' ||
        error.code === 'ECONNRESET' ||
        error.code === 'ERR_NETWORK' ||
        error.message?.toLowerCase().includes('timeout') ||
        error.message?.toLowerCase().includes('network') ||
        error.message?.toLowerCase().includes('fetch')
      ) {
        setErrorType('server-timeout');
      } else {
        setErrorType('default');
      }
    }
  };
  useEffect(() => {
    if (!book_id) return;

    const id = book_id.toString();
    getBookDetailsById(id);
    getAllChaptersListData(id);

    if (samplePdfValue) {
      handleGetSampleBookById(id);
    } else {
      handleGetDefaultBookById(id);
    }
  }, [book_id, samplePdfValue, chapterId, chapterPageNumber]);

  const handleDownload = () => {
    generateAndDownloadPdf({
      notes: Object.values(notes)
        .flat()
        .map((note) => ({
          ...note,
        })),
      title: booksDetails?.title || 'Zebralearn Book Notes',
      author:
        booksDetails?.author_list?.map((author) => author.name).join(', ') ||
        'Zebralearn',
      landingPageUrl: `${process.env.NEXT_PUBLIC_APP_ENV === 'production'
        ? 'https://zebralearn.com'
        : 'https://staging.zebralearn.com'
        }/books/${booksDetails?.slug}-${book_id}` as string,
    });
    toggleFullScreen();
  };

  // const swipeHandlers = useSwipeable({
  //   onSwipedLeft: async (eventData) => {
  //     try {
  //       const swipeDistance = Math.abs(eventData.deltaX);
  //       const threshold = window.innerWidth * 0.5;
  //       if (swipeDistance >= threshold) {
  //         if (!(pageNumber >= 10 && !token)) {
  //           const nextPage = await getNextPage();
  //           handlePageTransition(nextPage as number);
  //         }
  //       }
  //     } catch (error) {
  //       console.error('Error while handling swipe left:', error);
  //     }
  //   },
  //   onSwipedRight: async (eventData) => {
  //     try {
  //       const swipeDistance = Math.abs(eventData.deltaX);
  //       const threshold = window.innerWidth * 0.5;
  //       if (swipeDistance >= threshold) {
  //         const previousPage = await getPreviousPage();
  //         handlePageTransition(previousPage);
  //       }
  //     } catch (error) {
  //       console.error('Error while handling swipe right:', error);
  //     }
  //   },

  //   trackMouse: true,
  // });

  const onChapterHandler = async (chapterId: string, startPage?: number) => {
    setChapterFieldId(chapterId);
    setStartPage(startPage as number);
    setPageNumber(startPage as number);

    const pageData = completeBookTimestamps?.timeStamp.find(
      (item) =>
        item.page === (startPage || 0) - completeBookTimestamps?.startingPage,
    );

    if (pageData) {
      setSeekTime(pageData.start);
    }
  };

  const fullScreenIcon = (
    <div className='group'>
      <button
        onClick={toggleFullScreen}
        className={clsx(
          isFullScreen ? ' text-primary' : 'text-white ',
          'flex items-center  justify-center relative cursor-pointer g p-0   lg:p-2 w-6 h-6 md:w-10 md:h-10 rounded-full',
        )}
      >
        {!isFullScreen ? <EnterFullScreen /> : <ExitFullScreen />}
      </button>
    </div>
  );

  const handleZoomChange = (newScale: number) => {
    // const clampedScale = Math.min(Math.max(newScale, 1.0), 5.0);
    setScale(newScale);
    setZoomValue(Math.round(newScale * 100));
  };

  // const handleZoom = (event: any) => {
  //   const newScale = event?.state?.scale;
  //   if (newScale !== undefined) {
  //     handleZoomChange(newScale);
  //   }
  // };
  useEffect(() => {
    if (!screens.lg) {
      setSideBySide(false);
    } else {
      if (!openDrawer) {
        setSideBySide(true);
      }
    }
  }, [screens]);

  useEffect(() => {
    if (numPages && pageNumber + 1 > (numPages as number) && sideBySide) {
      setSideBySide(false);
    }

    if (!screens.md) {
      setVisible(false);
    }
  }, [pageNumber, startPage, sideBySide, numPages, screens, pageDimensions]);

  useEffect(() => {
    const handleTouchStart = () => {
      setIsInteracted(true);
    };

    const handleTouchMove = () => {
      setIsInteracted(true);
    };

    const handleTouchEnd = () => {
      setIsInteracted(true);
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsInteracted(false);
    }, 5000);

    return () => clearTimeout(timeout);
  }, [isInteracted]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      setWindowHeight(container.clientHeight);
      setWindowWidth(container.clientWidth);
      !isFullScreen && toggleFullScreen();
    }
  }, []);

  const isLandscapeMode = useMemo(() => {
    return pageDimensions.width > pageDimensions.height;
  }, [pageDimensions]);

  const fetchData = async () => {
    try {
      const token = await getLoginToken();
      setToken(token as string);
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };

  // const drawWatermark = (canvas: HTMLCanvasElement) => {
  //   const ctx = canvas.getContext('2d');
  //   if (!ctx) return;

  //   const text = userData?.zl_uid ?? 'ZebraLearn Confidential';
  //   const fontSize = 14;

  //   ctx.save();
  //   ctx.font = `${fontSize}px Arial`;
  //   ctx.globalAlpha = 0.3;
  //   ctx.fillStyle = '#000';

  //   const padding = 10;
  //   const textWidth = ctx.measureText(text).width;
  //   const textHeight = fontSize;

  //   const positions = [
  //     { x: padding, y: textHeight + padding },
  //     { x: canvas.width - textWidth - padding, y: textHeight + padding },
  //     { x: padding, y: canvas.height - padding },
  //     { x: canvas.width - textWidth - padding, y: canvas.height - padding },
  //   ];

  //   const randomPosition =
  //     positions[Math.floor(Math.random() * positions.length)];
  //   if (randomPosition) ctx.fillText(text, randomPosition.x, randomPosition.y);

  //   ctx.restore();
  // };

  useEffect(() => {
    fetchData();

    if (leftButtonRef.current) {
      leftButtonRef.current.focus();
    }
    if (rightButtonRef.current) {
      rightButtonRef.current.focus();
    }

    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [isTokenChanged]);

  // Ai Drawer

  const onCloseDrawer = () => {
    setOpenDrawer(false);
  };

  const onOpenDrawer = () => {
    setOpenDrawer(!openDrawer);
    if (screens.lg) {
      if (!openDrawer) {
        setSideBySide(false);
      } else {
        setSideBySide(true);
      }
    }
  };
  const hasVideo = useMemo(() => {
    return chaptersList?.chapters.some((item: Chapter) => !!item.videoUrl);
  }, [chaptersList]);



  // ─── Reading Tracker — unique-page deduplication ──────────────────────────
  // visitedPagesRef : every page the user has seen during this session (never reset).
  // pendingPagesRef : pages seen since the last tick (cleared after each tick).
  const visitedPagesRef = useRef<Set<number>>(new Set());
  const pendingPagesRef = useRef<Set<number>>(new Set());

  // Whenever the reader moves to a new page (or the layout switches), record
  // the currently visible page(s) in pendingPages.
  // In side-by-side mode two pages are shown at once, so both count.
  useEffect(() => {
    if (pageNumber) {
      pendingPagesRef.current.add(pageNumber);
      if (sideBySide && numPages && pageNumber + 1 <= numPages) {
        pendingPagesRef.current.add(pageNumber + 1);
      }
    }
  }, [pageNumber, sideBySide, numPages]);

  /**
   * Called by useReadingTracker on every 30-second tick.
   *
   * Logic:
   *  - Find pages in `pendingPages` that are NOT yet in `visitedPages`  → these are truly new.
   *  - Add those new pages to `visitedPages`.
   *  - Clear `pendingPages` so the next interval starts fresh.
   *  - Return the count of new pages (sent as `pages_read` in the tick payload).
   *
   * Example (100-page book):
   *   Tick 1 — user visited [1,2,3,4]      → sends 4  (visitedPages = {1,2,3,4})
   *   Tick 2 — user visited [3,4,5,6]      → sends 2  (only 5,6 are new)
   */
  const getPagesRead = useCallback((): number => {
    const newPages: number[] = [];
    pendingPagesRef.current.forEach((page) => {
      if (!visitedPagesRef.current.has(page)) {
        newPages.push(page);
        visitedPagesRef.current.add(page);
      }
    });
    pendingPagesRef.current.clear();

    return newPages.length;
  }, []);

  // ─── Reading Tracker (WebSocket session) ──────────────────────────────────
  const { startSession, stopSession } = useReadingTracker({
    source: 'books',
    sourceType: booksDetails?.type as ReadingSourceType,
    zlUid: userData?.zl_uid ?? '',
    sourceId: book_id?.toString() ?? '',
    sourceName: booksDetails?.title ?? '',
    token: token || undefined,
    subSourceId: useCurrentChapter?._id,
    subSourceName: useCurrentChapter?.name,
    currentPage: pageNumber.toString(),
    getPagesRead,
  });

  // Start session on mount, stop on unmount
  useEffect(() => {
    startSession();
    return () => {
      stopSession('user_closed');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    pageStartTimeRef.current = moment().toISOString();
    pageTimeSpentRef.current = 0;
    const interval = setInterval(() => {
      pageTimeSpentRef.current += 1;
    }, 1000);
    return () => clearInterval(interval);
  }, [pageNumber]);




  // details_view Events
  useEffect(() => {
    if (userData && booksDetails && book_id) {
      EventV2({
        payload: {
          event_name: 'learn_view',
          source: 'books',
          source_type: booksDetails?.type as any,
          source_id: book_id?.toString() ?? '',
          source_name: booksDetails?.ecomData?.display_name ? booksDetails.ecomData.display_name : booksDetails?.title ?? '',
          unit: 'boolean',
          value: 1,
          meta: {
            product_category: booksDetails?.categories?.[0]?.name,
            book_total_pages: numPages,
          }
        },
        userData: userData,
        pathname: router.pathname
      })
    };
  }, [userData, booksDetails, book_id])


  // do active session implementation


  return (
    <div ref={fullscreenRootRef} className='relative flex ant-layout-pdf h-full'>
      <Layout
        className={clsx(
          ' bg-bgColor h-full relative hide-scrollbar',
          isFullScreen ? ' min-h-[100vh]' : ' min-h-[85vh]',
        )}
        ref={containerRef}
      >
        <PdfReaderHeader
          isFullScreen={isFullScreen}
          toggleFullScreen={toggleFullScreen}
          zoomValue={zoomValue}
          zoomOut={zoomOut}
          zoomIn={zoomIn}
          sideBySide={sideBySide}
          title={booksDetails?.title || ''}
          rotateLeft={rotateLeft}
          pageNumber={pageNumber}
          rotateRight={rotateRight}
          toggleSideBySide={toggleSideBySide}
          openDrawer={openDrawer}
          hardcoverProductId={
            booksDetails?.hardcoverProductId ||
            booksDetails?.hardCoverProductId ||
            booksDetails?.productId
          }
          isHardCoverAvailable={booksDetails?.hardCoverAvailable}
        />

        <Content>
          <div
            className={clsx(
              'bg-bgColor bg-opacity-75 backdrop-filter hide-scrollbar backdrop-blur-md overflow-auto relative flex min-h-[85vh]',
              isFullScreen ? 'min-h-[100vh]  mt-14 ' : 'min-h-[85vh]',
            )}
            style={{
              scrollBehavior: 'smooth',
              scrollbarWidth: 'none',
            }}
          >
            <div className='w-full'>
              <div className='flex relative h-full items-center overflow-hidden hide-scrollbar'>
                <motion.div
                  onContextMenu={(e) => e.preventDefault()}
                  ref={pdfContainerRef}
                  className='w-full flex justify-center items-center h-full hide-scrollbar relative '
                  animate={{
                    x: screens.lg ? (openDrawer ? '-25vw' : '0') : '',
                  }}
                  transition={{
                    type: 'spring',
                    stiffness: 100,
                    damping: 20,
                  }}
                >
                  {isGenerating ? (
                    <div className='absolute inset-0 z-50 flex items-center justify-center bg-bgColor'>
                      <LoadingV2
                        loaderColor='white'
                        messages={[
                          'Preparing your personalized PDF...',
                          'Almost there, just a few more seconds!',
                          'Getting everything ready for you.',
                        ]}
                      />
                    </div>
                  ) : hasError && !(samplePdf || renderPdf) ? (
                    errorType === 'server-timeout' ? (
                      <FailedToLoadPDF
                        title='Check Your Network'
                        description='Please try again later.'
                        primaryButtonText='Go Back Home'
                        primaryButtonLink='/'
                        secondaryButtonText='Refresh'
                        secondaryButtonLink='/'
                        contactText='Contact Support'
                        contactLink='/support'
                        imageSrc={NetworkError}
                      />
                    ) : (
                      <FailedToLoadPDF
                        title='Trouble loading this book?'
                        description='For a smoother reading experience, continue on the ZebraLearn app.'
                        primaryButtonText='Continue in App'
                        primaryButtonLink='/get-our-app'
                        secondaryButtonText='Retry loading'
                        contactText='Get Support'
                        contactLink='/support'
                        imageSrc={FailedToLoadPdf}
                      />
                    )
                  ) : (
                    <Document
                      className={clsx(
                        'pdf-container w-full h-full overflow-auto z-10 hide-scrollbar',
                      )}
                      renderMode='canvas'
                      file={file}
                      rotate={rotations[pageNumber]}
                      noData={
                        <FailedToLoadPDF
                          title='Trouble loading this book?'
                          description='For a smoother reading experience, continue on the ZebraLearn app.'
                          primaryButtonText='Continue in App'
                          primaryButtonLink='/get-our-app'
                          secondaryButtonText='Retry loading'
                          contactText='Get Support'
                          contactLink='/support'
                          imageSrc={FailedToLoadPdf}
                        />
                      }
                      error={
                        <FailedToLoadPDF
                          title='Trouble loading this book?'
                          description='For a smoother reading experience, continue on the ZebraLearn app.'
                          primaryButtonText='Continue in App'
                          primaryButtonLink='/get-our-app'
                          secondaryButtonText='Retry loading'
                          contactText='Get Support'
                          contactLink='/support'
                          imageSrc={FailedToLoadPdf}
                        />
                      }
                      loading={
                        <LoadingV2
                          loaderColor='white'
                          messages={[
                            'Almost there, thanks for your patience!',
                            'Getting everything set up for you.',
                            'Your content is on its way!',
                            "Just a sec, making sure everything's perfect.",
                            "Hold on, we're almost ready!",
                            "Loading... we'll be with you shortly.",
                            "Thanks for waiting, we're preparing your journey!",
                          ]}
                        />
                      }
                      onLoadSuccess={onDocumentLoadSuccess}
                      options={options}
                      onLoadError={(error) => {
                        console.error('Document load error:', error);
                        setHasError(true);
                        if (
                          error.message?.toLowerCase().includes('timeout') ||
                          error.message?.toLowerCase().includes('network') ||
                          error.message?.toLowerCase().includes('fetch')
                        ) {
                          setErrorType('server-timeout');
                        } else {
                          setErrorType('default');
                        }
                      }}
                    >
                      <div
                        className={clsx(
                          isFullScreen && 'items-center',
                          'flex justify-center w-full md:items-center overflow-auto h-full hide-scrollbar',
                        )}
                      >
                        <TransformWrapper
                          ref={transformRef}
                          panning={{
                            disabled: false,
                            velocityDisabled: true,
                            excluded: ['exclude-panning'],
                          }}
                          pinch={{ disabled: false }}
                          doubleClick={{ disabled: true }}
                          wheel={{
                            disabled: true,
                          }}
                          centerOnInit
                          centerZoomedOut
                          zoomAnimation={{
                            animationType: 'linear',
                            animationTime: 200,
                          }}
                        >
                          <TransformComponent
                            wrapperClass='overflow-auto hide-scrollbar'
                            contentClass='w-full h-full hide-scrollbar exclude-panning'
                          >
                            {sideBySide ? (
                              <motion.div
                                className='w-full flex justify-center items-center h-full data-no-pan exclude-panning hide-scrollbar'
                                initial={{ x: 0 }}
                                animate={controls}
                              >
                                <div className='flex justify-center items-center relative'>
                                  {[-4, -3, -2, -1, 0, 1, 2, 3, 4].map(
                                    (offset) => {
                                      const actualPageNumber =
                                        pageNumber + offset;

                                      if (
                                        actualPageNumber < 1 ||
                                        actualPageNumber > numPages
                                      )
                                        return null;

                                      const isHidden =
                                        offset !== 0 && offset !== 1;

                                      return (
                                        <motion.div
                                          key={actualPageNumber}
                                          className={`w-full flex relative z-10 ${offset === 0
                                            ? 'justify-end'
                                            : 'justify-start'
                                            } ${isHidden ? 'hidden' : ''} page`}
                                        >
                                          <Page
                                            pageNumber={actualPageNumber}
                                            scale={scale}
                                            className='bg-cartBgColor'
                                            height={windowHeight as number}
                                            loading={
                                              <div
                                                style={{
                                                  width: Math.floor(
                                                    windowWidth *
                                                    (screens.xl ? 0.4 : 0.5),
                                                  ),
                                                  minHeight: windowHeight,
                                                }}
                                                className='flex justify-center items-center h-full '
                                              >
                                                <Spin />
                                              </div>
                                            }
                                            width={Math.floor(
                                              windowWidth *
                                              (screens.xl ? 0.4 : 0.5),
                                            )}
                                          // onRenderSuccess={() => {
                                          //   const canvas =
                                          //     document.querySelector(
                                          //       `.react-pdf__Page[data-page-number="${actualPageNumber}"] canvas`,
                                          //     ) as HTMLCanvasElement;

                                          //   // if (canvas) {
                                          //   //   drawWatermark(canvas);
                                          //   // }
                                          // }}
                                          />

                                          <div
                                            style={{
                                              background:
                                                'linear-gradient(to right, transparent 0%,rgba(0,0,0,0.2) 46%,rgba(0,0,0,0.5) 49%,rgba(0,0,0,0.6) 50%,rgba(0,0,0,0.5) 51%,rgba(0,0,0,0.2) 52%,transparent 100%)',
                                            }}
                                            className={clsx(
                                              offset === 0
                                                ? ' -right-10'
                                                : ' -left-10',
                                              'absolute h-full w-20 z-40',
                                            )}
                                          ></div>
                                        </motion.div>
                                      );
                                    },
                                  )}
                                </div>
                              </motion.div>
                            ) : (
                              <motion.div
                                className='overflow-auto exclude-panning w-full h-full hide-scrollbar'
                                initial={{ x: 0, y: 0 }}
                                animate={controls}
                                style={{
                                  overflow: 'auto',
                                  height: '100%',
                                  width: '100%',
                                }}
                              >
                                <div className='flex items-center'>
                                  {[-2, -1, 0, 1, 2].map((offset) => {
                                    const actualPageNumber =
                                      pageNumber + offset;

                                    if (
                                      actualPageNumber < 1 ||
                                      actualPageNumber > numPages
                                    )
                                      return null;

                                    const isHidden = offset !== 0;

                                    return (
                                      <Page
                                        key={actualPageNumber}
                                        pageNumber={actualPageNumber}
                                        scale={scale}
                                        height={
                                          screens.lg
                                            ? windowHeight
                                            : isLandscapeMode
                                              ? windowHeight
                                              : undefined
                                        }
                                        width={
                                          screens.lg
                                            ? isLandscapeMode
                                              ? Math.floor(windowWidth * 0.7)
                                              : Math.floor(windowWidth * 0.35)
                                            : windowWidth
                                        }
                                        // onRenderSuccess={() => {
                                        //   const canvas = document.querySelector(
                                        //     `.react-pdf__Page[data-page-number="${actualPageNumber}"] canvas`,
                                        //   ) as HTMLCanvasElement;

                                        //   if (canvas) {
                                        //     drawWatermark(canvas);
                                        //   }
                                        // }}
                                        className={`bg-cartBgColor ${isHidden ? 'hidden' : ''}`}
                                      />
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </TransformComponent>
                        </TransformWrapper>
                      </div>
                    </Document>
                  )}
                  <SelectionTooltip
                    ignoreRefs={[
                      pageNavigatorRef,
                      pageInputRef,
                      takeNotePanelRef,
                    ]}
                    parentRef={pdfContainerRef}
                    setTakeNodeModal={setTakeNoteModal}
                    takeNoteModal={takeNoteModal}
                  />
                </motion.div>
              </div>
            </div>
            <ChapterDrawer
              visible={visible}
              onClose={onClose}
              samplePdf={!!samplePdf as boolean}
              book_id={book_id as string}
              baseUrl={baseUrl as string}
              Endpoints={Endpoints}
              booksDetails={booksDetails}
              pageNumber={pageNumber}
              numPages={numPages}
              chaptersList={chaptersList}
              chapterFieldId={chapterFieldId}
              onChapterHandler={onChapterHandler}
              isFullScreen={isFullScreen}
            />

            <NotesApp
              landingPageUrl={
                `${process.env.NEXT_PUBLIC_APP_ENV === 'production'
                  ? 'https://zebralearn.com'
                  : 'https://staging.zebralearn.com'
                }/books/${booksDetails?.slug}-${book_id}` as string
              }
              bookId={book_id as string}
              bookAuthor={booksDetails?.author_list
                ?.map((author) => author.name)
                .join(', ')}
              bookTitle={booksDetails?.title as string}
            />
            <ShouldRender check={isFullScreen && token}>
              <ShareButton
                isFullScreen={isFullScreen}
                isLoggedIN={!!userData?.zl_uid}
                isShortBook={!!samplePdfValue}
                type='no-icon'
                isPdfReader={true}
                productId={book_id as string}
              />
            </ShouldRender>

            <ShouldRender check={!screens.lg && token}>
              <ShareButton
                isFullScreen={isFullScreen}
                isLoggedIN={!!userData?.zl_uid}
                isShortBook={!!samplePdfValue}
                type='no-icon'
                isPdfReader={true}
                productId={book_id as string}
              />
            </ShouldRender>

            <div className='flex items-center'>
              {' '}
              <AiChatDrawer
                onCancel={onCloseDrawer}
                visible={openDrawer}
                isFullScreen={isFullScreen}
                bookData={{
                  name: booksDetails?.title as string,
                  image: `${baseUrl}/${Endpoints.getCoverImagesForBooks}/${book_id}`,
                }}
              />
            </div>
          </div>

          <Modal
            title='Add Bookmark'
            open={isModalOpen}
            onOk={handleOk}
            footer={
              <div className='flex gap-x-3 justify-end mt-4'>
                <ButtonDefault
                  size={1}
                  variant={ButtonVariants.WHITE}
                  onClick={handleCancel}
                >
                  <span className='px-2 py-1'>Cancel</span>
                </ButtonDefault>
                <ButtonDefault
                  size={1}
                  variant={ButtonVariants.PRIMARY}
                  disabled={label === ''}
                  onClick={() => {
                    addBookmark();
                  }}
                >
                  <span className='px-2 py-1'>Add Bookmark</span>
                </ButtonDefault>
              </div>
            }
            onCancel={handleCancel}
          >
            <div className='flex flex-col gap-y-1'>
              <span>Enter a label for this bookmark:</span>
              <span>
                <Input
                  name='label'
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  size='large'
                  className=' border border-gray-600'
                />
              </span>
            </div>
          </Modal>
          <ShouldRender
            check={
              audioBook?.bookId &&
              (audioBook?.token || samplePdfValue) &&
              showAudio
            }
          >
            <div style={{ display: 'block' }}>
              <AudioPlayer
                bookId={audioBook?.bookId as string}
                token={audioBook?.token as string}
                isSampleBook={!!samplePdfValue}
                chapterPageNumber={
                  Number(chapterPageNumber) -
                  (completeBookTimestamps?.startingPage || 0)
                }
                timestamps={completeBookTimestamps?.timeStamp}
                showAudio={showAudio}
                pageOffset={completeBookTimestamps?.startingPage || 0}
                sideBySide={sideBySide}
                setPageNumber={setPageNumber}
                setSeekTime={setSeekTime}
                seekTime={seekTime}
                bookImage={
                  `${baseUrl}/${Endpoints.getCoverImagesForBooks}/${book_id}` as string
                }
                currentChapter={{
                  _id: useCurrentChapter?._id as string,
                  chapterNumber: useCurrentChapter?.chapterNumber as number,
                  description: useCurrentChapter?.description as string,
                  name: useCurrentChapter?.name as string,
                  totalPages: useCurrentChapter?.totalPages as number,
                  startPage: useCurrentChapter?.startPage as number,
                  endPage: useCurrentChapter?.endPage as number,
                }}
                onOpenDrawer={onOpenDrawer}
                userData={userData as User}
                setPlaying={setPlaying}
                playing={playing}
                playedSeconds={playedSeconds}
                duration={duration}
                setDuration={setDuration}
                setPlayedSeconds={setPlayedSeconds}
                backward15Seconds={backward15Seconds}
                forward15Seconds={forward15Seconds}
                playerRef={playerRef}
              />
            </div>
          </ShouldRender>
        </Content>
        <ShouldRender
          check={
            (!openDrawer || screens.lg) &&
            (samplePdf ? numPages || 0 : numPages || 0) > 1 &&
            !showAudio &&
            !showAudio &&
            !drawerState.contentDrawer &&
            !drawerState.notesDrawer
          }
        >
          <PageNavigator
            pageNumber={pageNumber}
            numPages={numPages}
            setPageNumber={setPageNumber}
            getNextPage={getNextPage}
            getPreviousPage={getPreviousPage}
            leftButtonRef={leftButtonRef}
            rightButtonRef={rightButtonRef}
            wrapperRef={pageNavigatorRef}
          />
        </ShouldRender>
        <ShouldRender check={drawerState.notesDrawer && isFullScreen}>
          <div
            className={clsx(
              'fixed md:hidden bottom-0 left-0  z-50',
              'w-full lg:pb-5',
            )}
          >
            <div className='bg-bgColor border-t-[1px] lg:border-none border-[#2A2A2A] md:px-6 items-center translate-y-1 p-2 xs:p-3 lg:p-0  flex'>
              <ButtonDefault
                size={2}
                onClick={handleDownload}
                variant={ButtonVariants.WHITE}
                className='w-full  whitespace-nowrap'
              >
                <div className='py- px-4'>Download as PDF</div>
              </ButtonDefault>
            </div>
          </div>
        </ShouldRender>
      </Layout>
      <ShouldRender
        check={
          !openDrawer ||
          (isFullScreen &&
            !drawerState.contentDrawer &&
            !drawerState.notesDrawer)
        }
      >
        <div className='h-full'>
          <ToolBoxSidebar
            fullScreenIcon={fullScreenIcon}
            isFullScreen={isFullScreen}
            isIphone={isIphone}
            showDrawer={showDrawer}
            toggleFullScreen={toggleFullScreen}
            userData={userData as User}
            visible={visible}
            key={`toolbox-${book_id}`}
            playAudio={playAudio}
            showAudio={showAudio}
            token={token}
            redirectToVideoPage={redirectToVideoPage}
            onOpenDrawer={onOpenDrawer}
            book_id={book_id as string}
            book_name={booksDetails?.title as string}
            showVideoBook={hasVideo}
            hideAudioBook={
              !!(
                (booksDetails?.completeBookAudio &&
                  booksDetails?.completeBookAudio !== 'null') ||
                (booksDetails?.audioBookSample &&
                  booksDetails?.audioBookSample !== 'null') ||
                (booksDetails?.sampleBookAudio &&
                  booksDetails?.sampleBookAudio !== 'null')
              )
            }
            askAiActive={booksDetails?.askAiActive}
          />
        </div>
      </ShouldRender>
      <TakeNotePanel
        open={takeNoteModal.value}
        onCancel={() => {
          setTakeNoteModal({
            value: false,
            pageNumber: 0,
            selectedText: '',
            selectionRect: null,
          });
          if (typeof window !== 'undefined') {
            const selection = window.getSelection?.();
            if (selection?.rangeCount) selection.removeAllRanges();
          }
        }}
        takeNotePanelRef={takeNotePanelRef}
        pdfUrl={samplePdf ?? renderPdf}
        book_id={book_id as string}
        chapterNumber={useCurrentChapter?.chapterNumber}
        pageNumber={takeNoteModal.pageNumber || 1}
        selectedText={takeNoteModal.selectedText}
        containerHeight={windowHeight as number}
        containerWidth={windowWidth as number}
        chapterId={useCurrentChapter?._id}
        coordinates={takeNoteModal.selectionRect}
        isFullScreen={isFullScreen}
        options={options}
      />
      <ShopModal open={openShopModal} setOpen={setOpenShopModal} />
      <CallbackModal
        visible={isCallbackModal.value}
        onClose={() =>
          setIsCallbackModal({
            productId: 0,
            value: false,
          })
        }
        product={isCallbackModal.productId}
        zl_uid={userData?.zl_uid}
      />

      <Modal
        centered
        title="Have you completed the book?"
        open={isCompletedModalOpen}
        onOk={() => {
          const eventParams: EventDetails = {
            event_name: 'complete',
            source: 'books',
            source_type: router.query['cs']
              ? 'comingSoon'
              : router.query['swp']
                ? 'short'
                : samplePdf
                  ? 'short'
                  : 'long',
            source_id: book_id as string,
            sub_source: samplePdf ? 'sample' : null,
            sub_source_id: samplePdf ? null : chapterFieldId,
            unit: 'boolean',
            source_name: booksDetails?.title
              ? normalizeProductName(booksDetails?.title)
              : booksDetails?.title,
            value: 1,
            meta: {
              book_page_no: samplePdf ? pageNumber : startPage + pageNumber - 1,
              book_total_pages: numPages,
              ...(!samplePdf && { chapter_page_no: pageNumber }),
            },
          };

          logEvent({
            eventDetails: eventParams,
            userData: userData,
            pathname: router.pathname,
          });

          setIsCompletedModalOpen(false);
        }}
        onCancel={() => setIsCompletedModalOpen(false)}
        okText="Completed"
        cancelText="No"
      />

      {/* ─── Video Book Modal ─────────────────────────────────────────── */}
      <Modal
        open={showVideoModal}
        onCancel={() => setShowVideoModal(false)}
        footer={null}
        width='100vw'
        style={{ top: 0, padding: 0, margin: 0, maxWidth: '100vw' }}
        styles={{
          body: {
            padding: 0,
            margin: 0,
            height: '100vh',
            background: '#09090b',
            overflow: 'hidden',
          },
          content: {
            padding: 0,
            borderRadius: 0,
            height: '100vh',
          },
        }}
        className='video-book-modal'
        destroyOnHidden
        getContainer={() => fullscreenRootRef.current ?? document.body}
      >
        <div className='text-white w-full h-full min-h-screen bg-grayZl-950 flex'>
          <PlayerWithChapters
            bookId={book_id?.toString()}
            chapterId={useCurrentChapter?._id}
            onBack={() => setShowVideoModal(false)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default PDFViewer;
